import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Database, Monitor, Settings, ShoppingCart, BarChart3 } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1764795850248-97a5e986b242?w=1920&q=80';

export function RetailTechPage() {
  const { i18n } = useTranslation();

  const services = [
    {
      number: '01',
      icon: ShoppingCart,
      title: i18n.language === 'fr' ? 'Solutions POS' : i18n.language === 'ar' ? 'حلول نقاط البيع' : 'POS Solutions',
      desc: i18n.language === 'fr' 
        ? 'Systèmes de point de vente sur mesure, intégration hardware et software.'
        : i18n.language === 'ar'
        ? 'أنظمة نقاط بيع مخصصة، تكامل الأجهزة والبرمجيات.'
        : 'Custom point-of-sale systems, hardware and software integration.'
    },
    {
      number: '02',
      icon: Database,
      title: i18n.language === 'fr' ? 'Back-Office' : i18n.language === 'ar' ? 'إدارة خلفية' : 'Back-Office',
      desc: i18n.language === 'fr'
        ? 'Gestion des stocks, CRM, reporting et analytics pour retailers.'
        : i18n.language === 'ar'
        ? 'إدارة المخزون، CRM، التقارير والتحليلات لتجار التجزئة.'
        : 'Inventory management, CRM, reporting and analytics for retailers.'
    },
    {
      number: '03',
      icon: Monitor,
      title: i18n.language === 'fr' ? 'Affichage Dynamique' : i18n.language === 'ar' ? 'العرض الديناميكي' : 'Digital Signage',
      desc: i18n.language === 'fr'
        ? 'Solutions d\'affichage en magasin, gestion de contenu centralisée.'
        : i18n.language === 'ar'
        ? 'حلول العرض في المتجر، إدارة المحتوى المركزية.'
        : 'In-store display solutions, centralized content management.'
    },
    {
      number: '04',
      icon: Code,
      title: i18n.language === 'fr' ? 'Intégrations API' : i18n.language === 'ar' ? 'تكامل API' : 'API Integrations',
      desc: i18n.language === 'fr'
        ? 'Connexion avec vos systèmes existants, développement sur mesure.'
        : i18n.language === 'ar'
        ? 'الاتصال بأنظمتك الحالية، التطوير المخصص.'
        : 'Connection with your existing systems, custom development.'
    }
  ];

  const approach = [
    { 
      title: i18n.language === 'fr' ? 'Analyse' : i18n.language === 'ar' ? 'تحليل' : 'Analysis',
      desc: i18n.language === 'fr' ? 'Audit de vos processus actuels' : i18n.language === 'ar' ? 'تدقيق عملياتك الحالية' : 'Audit of your current processes'
    },
    { 
      title: i18n.language === 'fr' ? 'Conception' : i18n.language === 'ar' ? 'تصميم' : 'Design',
      desc: i18n.language === 'fr' ? 'Architecture et UX sur mesure' : i18n.language === 'ar' ? 'هندسة وتجربة مستخدم مخصصة' : 'Custom architecture and UX'
    },
    { 
      title: i18n.language === 'fr' ? 'Développement' : i18n.language === 'ar' ? 'تطوير' : 'Development',
      desc: i18n.language === 'fr' ? 'Développement agile et itératif' : i18n.language === 'ar' ? 'تطوير مرن ومتكرر' : 'Agile and iterative development'
    },
    { 
      title: i18n.language === 'fr' ? 'Déploiement' : i18n.language === 'ar' ? 'نشر' : 'Deployment',
      desc: i18n.language === 'fr' ? 'Installation et formation' : i18n.language === 'ar' ? 'التثبيت والتدريب' : 'Installation and training'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div data-testid="retailtech-page">
      {/* Hero Section with background */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="retailtech-hero">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Retail Tech" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        
        <div className="container-main relative z-10 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-label text-gold mb-6">
              {i18n.language === 'fr' ? 'BUREAU D\'ÉTUDE' : i18n.language === 'ar' ? 'مكتب الدراسات' : 'ENGINEERING OFFICE'}
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8">
              {i18n.language === 'fr' ? (
                <>
                  Retail<br/>
                  <span className="text-gold">Tech</span>
                </>
              ) : i18n.language === 'ar' ? (
                <>
                  تقنية<br/>
                  <span className="text-gold">التجزئة</span>
                </>
              ) : (
                <>
                  Retail<br/>
                  <span className="text-gold">Tech</span>
                </>
              )}
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              {i18n.language === 'fr'
                ? 'Solutions logicielles innovantes pour optimiser vos opérations retail et améliorer l\'expérience client.'
                : i18n.language === 'ar'
                ? 'حلول برمجية مبتكرة لتحسين عمليات البيع بالتجزئة وتحسين تجربة العملاء.'
                : 'Innovative software solutions to optimize your retail operations and improve customer experience.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-dark section-xl" data-testid="services-section">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="mb-16"
          >
            <motion.p variants={itemVariants} className="text-label text-gold mb-4">
              {i18n.language === 'fr' ? 'NOS SERVICES' : i18n.language === 'ar' ? 'خدماتنا' : 'OUR SERVICES'}
            </motion.p>
            <motion.h2 variants={itemVariants} className="text-display text-white">
              {i18n.language === 'fr' ? 'Solutions retail' : i18n.language === 'ar' ? 'حلول البيع بالتجزئة' : 'Retail solutions'}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid md:grid-cols-2 gap-6"
          >
            {services.map((service) => (
              <motion.div 
                key={service.number} 
                variants={itemVariants}
                className="card-numbered-dark group"
              >
                <span className="card-number">{service.number}</span>
                <div className="flex items-center gap-3 mb-4">
                  <service.icon className="w-6 h-6 text-gold" />
                  <h3 className="text-heading text-white">{service.title}</h3>
                </div>
                <p className="text-muted text-body">{service.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section-cream section-xl" data-testid="approach-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-label text-gold mb-4">
                {i18n.language === 'fr' ? 'NOTRE APPROCHE' : i18n.language === 'ar' ? 'نهجنا' : 'OUR APPROACH'}
              </p>
              <h2 className="text-display mb-8">
                {i18n.language === 'fr' ? (
                  <>Méthodologie <span className="text-gold">agile</span></>
                ) : i18n.language === 'ar' ? (
                  <>منهجية <span className="text-gold">مرنة</span></>
                ) : (
                  <><span className="text-gold">Agile</span> methodology</>
                )}
              </h2>
              <p className="text-body-lg text-muted">
                {i18n.language === 'fr'
                  ? 'Une approche itérative et collaborative pour des solutions parfaitement adaptées à vos besoins.'
                  : i18n.language === 'ar'
                  ? 'نهج متكرر وتعاوني لحلول مناسبة تمامًا لاحتياجاتك.'
                  : 'An iterative and collaborative approach for solutions perfectly adapted to your needs.'}
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
              className="space-y-6"
            >
              {approach.map((step, index) => (
                <motion.div 
                  key={step.title}
                  variants={itemVariants}
                  className="flex items-start gap-6"
                >
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                    <span className="text-gold font-bold">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{step.title}</h4>
                    <p className="text-muted text-sm">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark section-lg" data-testid="retailtech-cta">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-display text-white mb-6">
              {i18n.language === 'fr' ? (
                <>Transformez votre <span className="text-gold">retail</span></>
              ) : i18n.language === 'ar' ? (
                <>حوّل <span className="text-gold">تجارتك</span></>
              ) : (
                <>Transform your <span className="text-gold">retail</span></>
              )}
            </h2>
            <p className="text-white/60 text-body-lg max-w-xl mx-auto mb-10">
              {i18n.language === 'fr'
                ? 'Discutons de vos enjeux et trouvons la solution adaptée.'
                : i18n.language === 'ar'
                ? 'دعنا نناقش تحدياتك ونجد الحل المناسب.'
                : 'Let\'s discuss your challenges and find the right solution.'}
            </p>
            <Link to="/contact" className="btn-gold">
              {i18n.language === 'fr' ? 'Parlons de votre projet' : i18n.language === 'ar' ? 'لنتحدث عن مشروعك' : 'Let\'s talk about your project'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default RetailTechPage;
